import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import FeatureRow from "../DeviceFeatures/FeatureRow";
import { Device } from "../Devices";
import DeviceNetworkPanel from "./DeviceNetworkPanel";
import { SideForm } from "../SideForms/SideForm";

import {
  RadioButtonGroup,
  TextInput,
  RadioButton,
  Form,
  FormGroup,
  TextInputSkeleton,
  TextAreaSkeleton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Link,
  StructuredListWrapper,
  StructuredListBody,
  SelectItem,
  Select,
  Toggle,
  Button,
  Slider,
  FormLabel,
  Tag,
  DatePicker,
  DatePickerInput,
} from "@carbon/react";
import { useNavigate } from "react-router-dom";
import { StreamDisplay } from "../Stream";
import { PanelLabel } from "../Panel";
import moment from "moment";
import {
  useGetDeviceQuery,
  useUpdateDeviceMutation,
  useGetDeviceStreamQuery,
  useCreateDeviceStreamMutation,
  useUpdateDeviceStreamMutation,
  useDeleteDeviceStreamMutation,
} from "@/redux/new_services/device";
import { useGetLocationsForSelectionQuery } from "@/redux/new_services/locations";
import useDeviceData from "@/lib/hooks/useDeviceStatistics";
import { hideForm } from "@/redux/new_services/forms";
import { useConfirmClosingForm, useOpenDeviceWizard, useSelectStream } from "@/lib/modal";
import { showToast } from "@/redux/new_services/toasts";
import useModal from "@/lib/hooks/useModal";
import useUser from "@/lib/hooks/useUser";

/**
 * get all redux actions
 *
 */
/*const actions = (dispatch) => {
  return {
    load: (item) => dispatch(deviceActions.get(item)),
    remove: (item) => dispatch(modalActions.confirmRemoveDevice(item)),
    close: () => dispatch(sideFormsActions.hideForm()),
    confirmClosingUnsavedForm: () =>
      dispatch(modalActions.confirmClosingUnsavedForm()),
    update: (form) => dispatch(deviceActions.update(form)),
    selectStream: (onStreamSelected) => {
      dispatch(streamsActions.selectStream(onStreamSelected));
    },
    getLocationsForSelection: () => {
      dispatch(locationActions.getForSelection());
    },
    showMirrorWizzard: (device) => {
      dispatch(
        modalActions.showMirrorWizzard({
          mirrorServerDeviceID: device.model.ID,
          mirrorServerName: device.label,
        })
      );
    },
  };
};*/

const DeviceForm = ({ id }) => {
  const dispatch = useDispatch();
  const confirmClosingUnsavedForm = useConfirmClosingForm();

  const { isAdmin } = useUser();

  const [showMirrorWizzard] = useModal("CREATE_MIRROR_DEVICE");
  const [confirmRemoveDevice] = useModal("DELETE_DEVICE");
  const selectStream = useSelectStream();

  const { data: device, isLoading, refetch } = useGetDeviceQuery(id);

  // Admin-only device-stream side-channel. The regular Device.Stream is
  // untouched by this; the device-stream is a separate Stream record that
  // an admin plans to manually plug into the regular stream's items.
  const navigate = useNavigate();
  const { data: deviceStreamResp } = useGetDeviceStreamQuery(id, { skip: !isAdmin || !id });
  const deviceStream = deviceStreamResp?.data || null;
  const [createDeviceStream] = useCreateDeviceStreamMutation();
  const [updateDeviceStream] = useUpdateDeviceStreamMutation();
  const [deleteDeviceStream] = useDeleteDeviceStreamMutation();

  const { features, mirrors, mqtt_snapshot: mqttSnapshot, data: item } = device || {};

  const { data: locations, isLoading: isLoadingLocations } = useGetLocationsForSelectionQuery();

  const [updateDevice, { isLoading: isSaving, error }] = useUpdateDeviceMutation();

  const {
    online: micast_online,
    dj,
    networks,
    access_points,
    scanWifi,
    connectWifi,
    disconnectWifi,
    forgetWifi,
    applyInterfaceConfig,
    setVolume,
  } = useDeviceData(item?.uuid, mqttSnapshot);

  const [audioVolume, setAudioVolume] = useState(dj?.audio_volume);
  const [streamVolume, setStreamVolume] = useState(dj?.stream_volume);
  const [offlineVolume, setOfflineVolume] = useState(dj?.offline_volume);
  const [spotVolume, setSpotVolume] = useState(dj?.spot_volume);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (dj.audio_volume && audioVolume != dj.audio_volume) {
      console.info("audioVolume changed:", audioVolume);
      const delayDebounceFn = setTimeout(() => {
        setVolume(stateItem.model.ID, "audio_volume", audioVolume);
      }, 400);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [audioVolume]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (dj.stream_volume && streamVolume != dj.stream_volume) {
      console.info("streamVolume changed:", streamVolume, dj.stream_volume);
      const delayDebounceFn = setTimeout(() => {
        setVolume(stateItem.model.ID, "stream_volume", streamVolume);
      }, 400);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [streamVolume]);

  const [stateItem, setItem] = useState(null);
  const [unsaved, setUnsaved] = useState(false);

  useEffect(() => {
    if (device && device.data) {
      //console.info("DeviceForm, item changed:", device.data);
      setItem(device.data);
    }
  }, [device]);

  /**
   * callback if a field in the form gets changed
   *
   * @param {any} field the name of the changed field
   * @param {any} value the new value of the changed field
   */
  const onChangeField = (field, value) => {
    setItem((prevValue) => ({
      ...prevValue,
      [field]: value,
    }));
    setUnsaved(true);
  };

  const onChangeSettingsField = (field, value) => {
    setItem((prevValue) => ({
      ...prevValue,
      settings: {
        ...prevValue.settings,
        [field]: value,
      },
    }));

    setUnsaved(true);
  };

  const handleSelectStream = () => {
    selectStream((stream) => {
      receiveNewStream(stream);
    });
  };

  const receiveNewStream = (stream) => {
    onChangeField("stream", stream);
  };

  const onRequestClose = () => {
    if (unsaved === true) {
      confirmClosingUnsavedForm(() => dispatch(hideForm()));
    } else {
      dispatch(hideForm());
    }
  };

  const onSaveRequest = () => {
    if (unsaved === true) {
      console.info("stateItem: ", stateItem);
      updateDevice({ id: stateItem.model.ID, form: stateItem })
        .unwrap()
        .then((result) => {
          console.info("update result:", result);
          dispatch(
            showToast({
              kind: "success",
              title: "Änderungen gespeichert",
              caption: "Micast wurde gespeichert.",
            }),
          );
          setUnsaved(false);
        })
        .catch((error) => {});
    } else {
      dispatch(hideForm());
    }
  };

  const clickMirrorWizzard = () => {
    showMirrorWizzard({
      mirrorServerDeviceID: device.model.ID,
      mirrorServerName: device.label,
    });
  };

  const on_change_future = useCallback(
    (name, new_settings) => {
      let settings =
        stateItem && stateItem.settings && stateItem.settings ? { ...stateItem.settings } : {};

      if (!settings.features) {
        settings.features = [];
      } else {
        settings.features = [...stateItem.settings.features];
      }

      const settingIndex = settings.features.findIndex((el) => el.name === name);

      if (settingIndex >= 0) {
        console.info("settings:", settings);
        settings.features[settingIndex] = new_settings;
      } else {
        settings.features.push(new_settings);
      }

      setItem((prevSettings) => ({
        ...prevSettings,
        settings: {
          ...settings,
        },
      }));

      setUnsaved(true);
    },
    [stateItem],
  );

  /*const add_feature = () => {
    addFeatureForm(stateItem, () => {});
  };*/

  let tabs;
  let stream_display;

  if (isLoading === true || isSaving || isLoadingLocations || stateItem === null) {
    // if no data are loaded / or data currently gets loaded

    // show Skeletons for all fields
    stream_display = <></>;

    tabs = (
      <Tabs>
        <TabList contained>
          <Tab id="tab_default">Allgemein</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Form>
              <FormGroup legendText="">
                <TextInputSkeleton />
              </FormGroup>
              <FormGroup legendText="">
                <TextAreaSkeleton />
              </FormGroup>
            </Form>
          </TabPanel>
        </TabPanels>
      </Tabs>
    );
  } else {
    // if a item is already loaded
    const settings = stateItem && stateItem.settings ? stateItem.settings : {};
    stream_display = (
      <StreamDisplay
        stream={stateItem.stream}
        orientation={stateItem.orientation}
        onSelectStream={handleSelectStream}
        nowebgl
      />
    );

    tabs = (
      <Tabs type="container">
        <TabList contained>
          <Tab id="tab_default">Allgemein</Tab>
          <Tab id="tab_settings">Einstellungen</Tab>
          <Tab disabled={!micast_online} id="tab_features">
            Audio
          </Tab>
          <Tab id="tab_features">Features</Tab>
          <Tab id="tab_advanced">Erweitert</Tab>
          <Tab id="tab_mirroring">Mirroring</Tab>
        </TabList>
        <TabPanels>
          <TabPanel className="tab-content">
            <Form>
              <FormGroup legendText="">
                <TextInput
                  labelText="Bezeichnung"
                  value={stateItem.label}
                  onChange={(e) => onChangeField("label", e.target.value)}
                  id="form_device_label"
                  helperText="Gebe dem System einen Namen damit du es später leichter findest."
                />
                <TextInput
                  readOnly={true}
                  labelText="System Bezeichnung"
                  value={stateItem.name}
                  id="form_device_name"
                  helperText="Die Micast-Geräte Bezeichnung wird automatisch festgelegt."
                />
                <TextInput
                  readOnly={true}
                  labelText="MAC-Adresse"
                  value={stateItem.mac_address}
                  id="form_device_mac_address"
                  helperText="Die Micast-Geräte Mac Adresse die im System hinterlegt ist"
                />
              </FormGroup>

              <PanelLabel semi style={{ paddingBottom: 0 }}>
                Zuletzt Online: {moment(stateItem.last_connection).format("DD.MM.YYYY HH:mm")}
              </PanelLabel>
            </Form>
          </TabPanel>
          <TabPanel className="tab-content">
            <Form>
              <FormGroup legendText="Ausrichtung">
                <RadioButtonGroup
                  valueSelected={stateItem.orientation + 1 || 1}
                  name="form_device_orientation"
                  onChange={(e) => onChangeField("orientation", e - 1)}
                >
                  <RadioButton value={1} labelText="Querformat" />
                  <RadioButton value={2} labelText="Hochkant Darstellung" />
                </RadioButtonGroup>
              </FormGroup>
            </Form>

            <FormGroup legendText="Laufschrift">
              <Toggle
                id="form_device_settings_show_scrolltext"
                labelText="Laufschrift Aktivieren"
                labelA="Deaktivert"
                labelB="Aktiviert"
                onToggle={(t, _i) => {
                  onChangeSettingsField("show_scrolltext", t === true ? 1 : 0);
                }}
                toggled={settings.show_scrolltext === 0 ? false : true}
              />
              <TextInput
                labelText="Text für Laufschrift"
                value={settings.scrolltext || ""}
                id="form_device_settings_scrolltext"
                onChange={(e) => onChangeSettingsField("scrolltext", e.target.value)}
              />
              <Toggle
                id="form_device_settings_show_clock"
                labelText="Uhrzeit Anzeigen"
                labelA="Deaktivert"
                labelB="Aktiviert"
                onToggle={(t, _i) => {
                  onChangeSettingsField("show_clock", t === true ? 1 : 0);
                }}
                toggled={settings.show_clock === 0 ? false : true}
              />
            </FormGroup>
            <FormGroup legendText="Theme">
              <Select
                id="theme-selection"
                labelText={"Welches Theme soll verwendet werden"}
                value={settings.theme || "default"}
                onChange={(e) => onChangeSettingsField("theme", e.currentTarget.value)}
                data-field="mode"
                data-valuetype="string"
              >
                <SelectItem key="theme-selection_default" text="Standard Theme" value="default" />
                <SelectItem key="theme-selection_vegas" text="Vegas Theme" value="vegas" />
                <SelectItem key="theme-selection_neon" text="Neon Theme" value="neon" />
              </Select>
              <br />
              <Toggle
                id="form_device_settings_background_video"
                labelText="Bundesliga Hintergrundbild als Video Anzeigen"
                labelA="Deaktivert"
                labelB="Aktiviert"
                onToggle={(t, _i) => {
                  onChangeSettingsField("statistic_background", t === true ? "video" : "image");
                }}
                toggled={settings.statistic_background === "video" ? true : false}
              />
            </FormGroup>
          </TabPanel>
          <TabPanel className="tab-content">
            <Form>
              <FormLabel>
                Die Veränderungen der Lautstärke werden direkt an das System weitergegeben!
              </FormLabel>
              <FormGroup legendText="Lautstärke Einstellungen">
                <Slider
                  id="form_device_settings_volume"
                  labelText="Gesamt Lautstärke"
                  min={0}
                  max={100}
                  value={Number(dj?.audio_volume)}
                  onChange={(e) => setAudioVolume(Number(e.value))}
                />
                <Slider
                  id="form_device_settings_volume"
                  labelText="Musik Lautstärke"
                  min={0}
                  max={100}
                  value={Number(dj?.stream_volume)}
                  onChange={(e) => setStreamVolume(Number(e.value))}
                />
              </FormGroup>
              <FormGroup legendText="Ausgabe">
                <RadioButtonGroup
                  valueSelected={stateItem.sound_output || "hdmi_out"}
                  name="form_device_sound_output"
                  onChange={(e) => onChangeField("sound_output", e)}
                >
                  <RadioButton value="hdmi_out" labelText="HDMI Ausgang" />
                  <RadioButton value="line_out" labelText="LineOut Ausgang" />
                </RadioButtonGroup>
              </FormGroup>
            </Form>
          </TabPanel>
          <TabPanel className="tab-content">
            <Form>
              <FormGroup legendText="">
                <StructuredListWrapper isCondensed isFlush>
                  <StructuredListBody>
                    {features.map((feature) => (
                      <FeatureRow
                        key={`feature_row_${feature.model.ID}`}
                        id={feature.model.ID}
                        name={feature.name}
                        description={feature.description}
                        onChange={on_change_future}
                        currentState={(settings.features || []).find(
                          (e) => e.name === feature.name,
                        )}
                      />
                    ))}
                  </StructuredListBody>
                </StructuredListWrapper>
              </FormGroup>
            </Form>
          </TabPanel>
          <TabPanel className="tab-content">
            <Form>
              <FormGroup legendText="In anderen Standort bewegen">
                <Select
                  id="form_create_device_location"
                  labelText="Standort wechsel"
                  value={stateItem.location_id}
                  onChange={(e) => onChangeField("location_id", parseInt(e.currentTarget.value))}
                  data-field="mode"
                  data-valuetype="string"
                >
                  <SelectItem text="Standort auswählen..." value={0} />
                  {(locations || []).map((i) => {
                    return (
                      <SelectItem
                        key={`form_mode_select_item_${i.value}`}
                        text={i.label}
                        value={i.value}
                      />
                    );
                  })}
                </Select>
              </FormGroup>
              {isAdmin && (
                <FormGroup legendText="Sonstiges">
                  <Toggle
                    id="form_device_settings_enable_gema"
                    labelText="GEMA freies Radio aktivieren"
                    labelA="Deaktivert"
                    labelB="Aktiviert"
                    onToggle={(t, _i) => {
                      onChangeSettingsField("enable_gema_stream", t === true ? 1 : 0);
                    }}
                    toggled={settings.enable_gema_stream === 0 ? false : true}
                  />
                </FormGroup>
              )}
              <hr />
              <Link href="#" onClick={() => confirmRemoveDevice({ device: stateItem })}>
                Gerät endgültig löschen
              </Link>
            </Form>
          </TabPanel>
          <TabPanel className="tab-content">
            <Link href="#" onClick={() => clickMirrorWizzard()}>
              Spiegel-System hinzufügen
            </Link>
            <hr />
            <div className="bx-row">
              <div className="cds--col-md-6 cds--col-lg-13" style={{ display: "flex" }}>
                {(mirrors || []).map((device) => (
                  <Device
                    expanded={true}
                    device={device}
                    is_mirror={true}
                    mirror_server_id={stateItem.model.ID}
                    key={`mirror_device_${device.model.ID}`}
                  />
                ))}
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    );
  }

  // Device-stream slot — rendered next to the regular stream display.
  // Admin-only. Three visual states:
  //   1. None yet → call-to-action "Geräte-Stream anlegen"
  //   2. Loaded → preview-like tile with name, "Bearbeiten" + "Entfernen" +
  //      optional active-window picker
  // The actual stream content (items) is edited via the regular stream
  // editor; we just open it in a new tab via navigate(). Time window
  // changes are submitted with a small "Übernehmen" button so they don't
  // get mixed into the parent device form's dirty flag.
  const handleCreateDeviceStream = useCallback(async () => {
    try {
      const res = await createDeviceStream({ deviceId: id }).unwrap();
      dispatch(showToast({
        kind: "success",
        title: "Geräte-Stream angelegt",
        caption: "Du kannst ihn jetzt mit Inhalten füllen.",
      }));
      const newId = res?.data?.model?.ID;
      if (newId) {
        dispatch(hideForm());
        navigate(`/stream/${newId}`);
      }
    } catch (e) {
      dispatch(showToast({
        kind: "error",
        title: "Anlegen fehlgeschlagen",
        caption: e?.data?.message || "Geräte-Stream konnte nicht angelegt werden.",
      }));
    }
  }, [createDeviceStream, id, dispatch, navigate]);

  const handleDeleteDeviceStream = useCallback(async () => {
    if (!deviceStream?.model?.ID) return;
    if (!window.confirm("Geräte-Stream wirklich entfernen? Die Inhalte gehen verloren.")) return;
    try {
      await deleteDeviceStream({ deviceId: id }).unwrap();
      dispatch(showToast({
        kind: "success",
        title: "Geräte-Stream entfernt",
        caption: "",
      }));
    } catch (e) {
      dispatch(showToast({
        kind: "error",
        title: "Löschen fehlgeschlagen",
        caption: e?.data?.message || "Geräte-Stream konnte nicht entfernt werden.",
      }));
    }
  }, [deleteDeviceStream, deviceStream, id, dispatch]);

  const [windowDirty, setWindowDirty] = useState(false);
  const [windowDraft, setWindowDraft] = useState({ from: "", to: "" });
  useEffect(() => {
    setWindowDraft({
      from: deviceStream?.active_from || "",
      to: deviceStream?.active_to || "",
    });
    setWindowDirty(false);
  }, [deviceStream?.active_from, deviceStream?.active_to]);

  const handleWindowApply = useCallback(async () => {
    try {
      await updateDeviceStream({
        deviceId: id,
        active_from: windowDraft.from ? new Date(windowDraft.from).toISOString() : null,
        active_to: windowDraft.to ? new Date(windowDraft.to).toISOString() : null,
      }).unwrap();
      dispatch(showToast({
        kind: "success",
        title: "Zeitfenster gespeichert",
        caption: "",
      }));
      setWindowDirty(false);
    } catch (e) {
      dispatch(showToast({
        kind: "error",
        title: "Speichern fehlgeschlagen",
        caption: e?.data?.message || "Zeitfenster konnte nicht gespeichert werden.",
      }));
    }
  }, [updateDeviceStream, id, windowDraft, dispatch]);

  const deviceStreamSlot = isAdmin && (
    <div
      style={{
        // Cap the slot at the parent's width so long text wraps cleanly
        // instead of pushing the form horizontally past its container.
        flex: "1 1 240px",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <FormLabel>
        Zusatz-Stream{" "}
        <Tag type="purple" size="sm">Geräte-Stream</Tag>
      </FormLabel>
      {deviceStream ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            marginTop: "0.5rem",
            minWidth: 0,
          }}
        >
          <div
            style={{
              padding: "0.75rem",
              border: "1px dashed var(--cds-border-strong)",
              borderRadius: 2,
              background: "var(--cds-layer-01)",
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{deviceStream.name}</div>
            <div style={{ fontSize: "0.75rem", color: "var(--cds-text-secondary)", marginTop: "0.25rem" }}>
              {(deviceStream.items || []).length} Element{(deviceStream.items || []).length === 1 ? "" : "e"}
            </div>
          </div>
          <DatePicker
            datePickerType="range"
            dateFormat="d.m.Y"
            locale="de"
            onChange={(values) => {
              setWindowDraft({
                from: values[0] ? values[0].toISOString() : "",
                to: values[1] ? values[1].toISOString() : "",
              });
              setWindowDirty(true);
            }}
            value={[
              windowDraft.from ? Date.parse(windowDraft.from) : null,
              windowDraft.to ? Date.parse(windowDraft.to) : null,
            ]}
          >
            <DatePickerInput
              id={`device_stream_active_from_${id}`}
              labelText="Aktiv von"
              placeholder="TT.MM.JJJJ"
              size="sm"
            />
            <DatePickerInput
              id={`device_stream_active_to_${id}`}
              labelText="Aktiv bis"
              placeholder="TT.MM.JJJJ"
              size="sm"
            />
          </DatePicker>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button
              kind="primary"
              size="sm"
              disabled={!windowDirty}
              onClick={handleWindowApply}
            >
              Zeitfenster übernehmen
            </Button>
            <Button
              kind="secondary"
              size="sm"
              onClick={() => {
                dispatch(hideForm());
                navigate(`/stream/${deviceStream.model.ID}`);
              }}
            >
              Inhalt bearbeiten
            </Button>
            <Button kind="danger--ghost" size="sm" onClick={handleDeleteDeviceStream}>
              Entfernen
            </Button>
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--cds-text-secondary)",
              overflowWrap: "anywhere",
              whiteSpace: "normal",
            }}
          >
            Wird vom System automatisch ans Ende des regulären Streams
            angehängt, sobald das Zeitfenster offen ist (oder dauerhaft,
            wenn keins gesetzt ist).
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            marginTop: "0.5rem",
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--cds-text-secondary)",
              overflowWrap: "anywhere",
              whiteSpace: "normal",
            }}
          >
            Admin-only. Ein eigener Stream nur für dieses Gerät — z. B. für
            zeitlich begrenzte Aktionen. Wird automatisch ans Ende des
            regulären Streams gehängt.
          </div>
          <Button kind="tertiary" size="sm" onClick={handleCreateDeviceStream}>
            Geräte-Stream anlegen
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <SideForm
      title="Micast Einstellungen"
      subTitle="ausgewähltes Micast-System bearbeiten"
      onClose={onRequestClose}
      onSubmit={onSaveRequest}
      primaryButton={"Speichern"}
      primaryButtonDisabled={!unsaved}
      secondaryButton={unsaved === true ? "Abbruch" : "Verlassen"}
    >
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "flex-start",
          flexWrap: "wrap",
          // Prevent the row from forcing horizontal scroll on narrow side
          // forms — both children shrink instead of pushing the row wider.
          minWidth: 0,
        }}
      >
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>{stream_display}</div>
        {deviceStreamSlot}
      </div>
      <br />
      {tabs}
    </SideForm>
  );
};

export default DeviceForm;
